import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../../application/AuthContext';
import { authRepository } from '../../infrastructure/authApiRepository';
import { PasswordToggleButton } from '../components/PasswordToggleButton';
import { jobOfferRepository } from '../../../landing/infrastructure/jobOfferApiRepository';
import { jobApplicationRepository } from '../../../landing/infrastructure/jobApplicationApiRepository';
import { jobSeekerRequestRepository } from '../../../landing/infrastructure/jobSeekerRequestApiRepository';
import { recruitmentRequestRepository } from '../../../landing/infrastructure/recruitmentRequestApiRepository';
import '../styles/auth.css';

const APPLICATION_STATUS_LABEL = {
    APPLIED: '신청함',
    CANCELLED: '취소함',
};

function maskEmail(email) {
    if (!email) return '-';
    const atIdx = email.indexOf('@');
    if (atIdx <= 0) return email;
    const local = email.slice(0, atIdx);
    const domain = email.slice(atIdx);
    const visible = Math.min(2, local.length);
    return local.slice(0, visible) + '****' + domain;
}

const MENU = [
    { key: 'profile',        label: '프로필' },
    { key: 'my-seeker-posts',label: '내 구직글',   seekerOnly: true },
    { key: 'offers',         label: '받은 채용제안', seekerOnly: true },
    { key: 'myapplied',      label: '보낸 채용신청', seekerOnly: true },
    { key: 'my-recruit-posts',label: '내 구인글',   companyOnly: true },
    { key: 'received',       label: '받은 채용신청', companyOnly: true },
    { key: 'sent-offers',    label: '보낸 채용제안', companyOnly: true },
];

export function AccountPage() {
    const { member, accessToken, logout, clearAuth, refreshMember } = useAuthContext();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const activeKey = searchParams.get('tab') || 'profile';

    function setActiveKey(key) {
        setSearchParams({ tab: key }, { replace: false });
    }

    useEffect(() => {
        refreshMember();
    }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleUnauthorized = React.useCallback(() => {
        clearAuth();
        navigate('/login', { replace: true });
    }, [clearAuth, navigate]);

    useEffect(() => {
        if (!accessToken) {
            navigate('/login', { replace: true });
            return;
        }
        try {
            const payload = JSON.parse(atob(accessToken.split('.')[1]));
            if (payload.exp && payload.exp * 1000 < Date.now()) {
                clearAuth();
                navigate('/login', { replace: true });
            }
        } catch {
            clearAuth();
            navigate('/login', { replace: true });
        }
    }, [accessToken, clearAuth, navigate]);

    const isCompany   = member?.role === 'COMPANY';
    const isSeeker    = member?.role === 'JOB_SEEKER';

    const visibleMenu = MENU.filter(m => {
        if (m.seekerOnly   && !isSeeker)   return false;
        if (m.companyOnly  && !isCompany)  return false;
        return true;
    });

    async function handleLogout() {
        await logout();
        navigate('/landing', { replace: true });
    }

    return (
        <div className="ap-root">

            {/* ── 좌측 사이드바 (데스크톱) / 상단 탭바 (모바일) ── */}
            <aside className="ap-sidebar">
                <div className="ap-tab-list">
                    {visibleMenu.map(m => (
                        <button
                            key={m.key}
                            className={`ap-tab${activeKey === m.key ? ' ap-tab--active' : ''}`}
                            onClick={() => setActiveKey(m.key)}
                        >
                            {m.label}
                        </button>
                    ))}
                </div>

                {/* 데스크톱 전용 — 하단 로그아웃 */}
                <div className="ap-sidebar-footer">
                    <div className="ap-nav-divider" />
                    <button className="ap-tab ap-tab--logout" onClick={handleLogout}>
                        로그아웃
                    </button>
                </div>

                {/* 모바일 전용 — 우측 설정 아이콘 */}
                <button className="ap-tab-settings" onClick={handleLogout} title="로그아웃">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="3"/>
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                    </svg>
                </button>
            </aside>

            {/* ── 콘텐츠 ── */}
            <main className="ap-content">
                <div className="ap-content-inner">
                {activeKey === 'profile' && (
                    <ProfilePanel member={member} isCompany={isCompany} isSeeker={isSeeker} onTabChange={setActiveKey} />
                )}
                {activeKey === 'my-seeker-posts' && (
                    <MyJobSeekerPostsPanel accessToken={accessToken} onUnauthorized={handleUnauthorized} />
                )}
                {activeKey === 'offers' && (
                    <JobOffersPanel accessToken={accessToken} onUnauthorized={handleUnauthorized} />
                )}
                {activeKey === 'myapplied' && (
                    <MyApplicationsPanel accessToken={accessToken} onUnauthorized={handleUnauthorized} />
                )}
                {activeKey === 'my-recruit-posts' && (
                    <MyRecruitmentPostsPanel accessToken={accessToken} onUnauthorized={handleUnauthorized} />
                )}
                {activeKey === 'received' && (
                    <ReceivedApplicationsPanel accessToken={accessToken} onUnauthorized={handleUnauthorized} />
                )}
                {activeKey === 'sent-offers' && (
                    <SentJobOffersPanel accessToken={accessToken} onUnauthorized={handleUnauthorized} />
                )}
                </div>
            </main>
        </div>
    );
}

function ChevronRight() {
    return (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <polyline points="9 18 15 12 9 6"/>
        </svg>
    );
}

/* ── 프로필 패널 ─────────────────────────────────── */
function ProfilePanel({ member, isCompany, isSeeker, onTabChange }) {
    const { accessToken, clearAuth } = useAuthContext();
    const navigate = useNavigate();
    const roleLabel = isCompany ? '기업회원' : '개인회원';
    const initial = member?.email?.charAt(0).toUpperCase();

    const [withdrawOpen, setWithdrawOpen] = useState(false);
    const [withdrawPw, setWithdrawPw] = useState('');
    const [withdrawError, setWithdrawError] = useState('');
    const [withdrawing, setWithdrawing] = useState(false);
    const [showWithdrawPw, setShowWithdrawPw] = useState(false);

    function openWithdraw() {
        setWithdrawPw('');
        setWithdrawError('');
        setWithdrawOpen(true);
    }

    async function handleWithdraw() {
        setWithdrawError('');
        setWithdrawing(true);
        try {
            await authRepository.withdraw(accessToken, withdrawPw || undefined);
            clearAuth();
            navigate('/landing', { replace: true });
        } catch (err) {
            setWithdrawError(err.message || '탈퇴 처리에 실패했습니다.');
        } finally {
            setWithdrawing(false);
        }
    }

    return (
        <div className="ap-panel ap-dashboard">

            {/* ── 계정 헤더 ── */}
            <div className="ap-dh-card">
                <div className="ap-dh-avatar">{initial}</div>
                <div className="ap-dh-info">
                    <div className="ap-dh-name">{member?.email?.split('@')[0]}</div>
                    <div className="ap-dh-email">{member?.email}</div>
                </div>
                <span className={`ap-dh-role${isCompany ? ' ap-dh-role--company' : ''}`}>{roleLabel}</span>
            </div>

            {/* ── 내 활동 (구직자) ── */}
            {isSeeker && (
                <div className="ap-db-section">
                    <div className="ap-db-label">내 활동</div>
                    <div className="ap-db-tiles">
                        <button className="ap-db-tile" onClick={() => onTabChange('my-seeker-posts')}>
                            <span className="ap-db-tile-icon ap-icon--blue">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
                                </svg>
                            </span>
                            <span className="ap-db-tile-name">내 구직글</span>
                            <ChevronRight />
                        </button>
                        <button className="ap-db-tile" onClick={() => onTabChange('offers')}>
                            <span className="ap-db-tile-icon ap-icon--orange">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
                                </svg>
                            </span>
                            <span className="ap-db-tile-name">받은 채용제안</span>
                            <ChevronRight />
                        </button>
                        <button className="ap-db-tile" onClick={() => onTabChange('myapplied')}>
                            <span className="ap-db-tile-icon ap-icon--green">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                                </svg>
                            </span>
                            <span className="ap-db-tile-name">보낸 채용신청</span>
                            <ChevronRight />
                        </button>
                    </div>
                </div>
            )}

            {/* ── 내 활동 (기업) ── */}
            {isCompany && (
                <div className="ap-db-section">
                    <div className="ap-db-label">내 활동</div>
                    <div className="ap-db-tiles">
                        <button className="ap-db-tile" onClick={() => onTabChange('my-recruit-posts')}>
                            <span className="ap-db-tile-icon ap-icon--blue">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
                                </svg>
                            </span>
                            <span className="ap-db-tile-name">내 구인글</span>
                            <ChevronRight />
                        </button>
                        <button className="ap-db-tile" onClick={() => onTabChange('received')}>
                            <span className="ap-db-tile-icon ap-icon--orange">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>
                                </svg>
                            </span>
                            <span className="ap-db-tile-name">받은 채용신청</span>
                            <ChevronRight />
                        </button>
                        <button className="ap-db-tile" onClick={() => onTabChange('sent-offers')}>
                            <span className="ap-db-tile-icon ap-icon--green">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                                </svg>
                            </span>
                            <span className="ap-db-tile-name">보낸 채용제안</span>
                            <ChevronRight />
                        </button>
                    </div>
                </div>
            )}

            {/* ── 기업 관리 (기업 회원) ── */}
            {isCompany && (
                <div className="ap-db-section">
                    <div className="ap-db-label">기업 관리</div>
                    <div className="ap-list">
                        <Link to="/company/profile" className="ap-list-item">
                            <span>기업 프로필</span>
                            <span className="ap-arrow">›</span>
                        </Link>
                    </div>
                </div>
            )}

            {/* ── 계정 설정 ── */}
            <div className="ap-db-section">
                <div className="ap-db-label">계정 설정</div>
                <div className="ap-list">
                    <Link to="/forgot-password" className="ap-list-item">
                        <span>비밀번호 변경</span>
                        <span className="ap-arrow">›</span>
                    </Link>
                    <button className="ap-list-item ap-list-item--danger" onClick={openWithdraw}>
                        <span>회원 탈퇴</span>
                    </button>
                </div>
            </div>

            {/* ── 탈퇴 확인 모달 ── */}
            {withdrawOpen && (
                <div className="ap-modal-overlay" onClick={() => setWithdrawOpen(false)}>
                    <div className="ap-modal" onClick={e => e.stopPropagation()}>
                        <div className="ap-modal-title">회원 탈퇴</div>
                        <div className="ap-modal-body">
                            <div className="ap-modal-warn">
                                탈퇴하시면 구직글, 신청 이력 등 모든 데이터가 삭제되며 복구할 수 없습니다.
                            </div>
                            <div className="ap-modal-field">
                                <label className="ap-modal-label">비밀번호 확인</label>
                                <div className="auth-input-wrap">
                                    <input
                                        className={`ap-modal-input${withdrawError ? ' ap-modal-input--error' : ''}`}
                                        type={showWithdrawPw ? 'text' : 'password'}
                                        placeholder="현재 비밀번호를 입력해주세요"
                                        value={withdrawPw}
                                        onChange={e => { setWithdrawPw(e.target.value); setWithdrawError(''); }}
                                        onKeyDown={e => e.key === 'Enter' && !withdrawing && handleWithdraw()}
                                        autoFocus
                                    />
                                    <PasswordToggleButton visible={showWithdrawPw} onToggle={() => setShowWithdrawPw((v) => !v)} />
                                </div>
                            </div>
                            {withdrawError && <p className="ap-modal-error">{withdrawError}</p>}
                        </div>
                        <div className="ap-modal-actions">
                            <button className="ap-modal-btn ap-modal-btn--secondary" onClick={() => setWithdrawOpen(false)}>
                                취소
                            </button>
                            <button className="ap-modal-btn ap-modal-btn--danger" onClick={handleWithdraw} disabled={withdrawing}>
                                {withdrawing ? '처리 중...' : '탈퇴하기'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ── 채용제안 패널 ───────────────────────────────── */
function JobOffersPanel({ accessToken, onUnauthorized }) {
    const navigate = useNavigate();
    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!accessToken) { onUnauthorized(); return; }
        jobOfferRepository.getReceivedOffers(accessToken)
            .then(data => setOffers(data || []))
            .catch(e => {
                if (e.status === 401) { onUnauthorized(); return; }
                setError(e.message || '불러오기 실패');
            })
            .finally(() => setLoading(false));
    }, [accessToken, onUnauthorized]);

    return (
        <div className="ap-panel">
            <div className="ap-section-title">받은 채용제안</div>

            {loading && <div className="ap-offer-empty">불러오는 중...</div>}
            {!loading && error && <div className="ap-offer-empty" style={{ color: '#e53935' }}>{error}</div>}
            {!loading && !error && offers.length === 0 && (
                <div className="ap-offer-empty">아직 받은 채용제안이 없습니다.</div>
            )}
            {!loading && !error && offers.length > 0 && (
                <div className="ap-offer-list">
                    {offers.map(offer => (
                        <div key={offer.id} className={`ap-offer-card${offer.status === 'WITHDRAWN' ? ' ap-offer-card--withdrawn' : ''}`}>
                            <div className="ap-offer-card-header">
                                <div className="ap-offer-company">{offer.companyName || '회사명 미상'}</div>
                                <span className={`ap-recv-status ${offer.status === 'WITHDRAWN' ? 'ap-recv-status--rejected' : 'ap-recv-status--pass'}`}>
                                    {{ OFFERED: '제안 받음', WITHDRAWN: '제안 취소됨' }[offer.status] || '제안 받음'}
                                </span>
                            </div>
                            <div className="ap-offer-meta">
                                {offer.industry && <span className="ap-offer-tag">{offer.industry}</span>}
                                {offer.companyAddress && (
                                    <span className="ap-offer-tag">{offer.companyAddress.split(' ').slice(0, 2).join(' ')}</span>
                                )}
                            </div>
                            {offer.message && (
                                <div className="ap-offer-message">"{offer.message}"</div>
                            )}
                            <div className="ap-offer-footer">
                                <span className="ap-offer-contact">담당자 이메일: <strong>{maskEmail(offer.senderEmail)}</strong></span>
                                <div className="ap-offer-footer-right">
                                    <span className="ap-offer-date">{offer.sentAt ? String(offer.sentAt).slice(0, 10) : ''}</span>
                                    {offer.recruitmentRequestId && (
                                        <button className="ap-recv-link-btn" onClick={() => navigate(`/premium/job-board/hire/${offer.recruitmentRequestId}`)}>
                                            모집글 보기
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/* ── 내 신청 현황 패널 (JOB_SEEKER) ─────────────── */
function MyApplicationsPanel({ accessToken, onUnauthorized }) {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [cancelling, setCancelling] = useState(null);
    const [confirmItem, setConfirmItem] = useState(null);

    useEffect(() => {
        if (!accessToken) { onUnauthorized(); return; }
        jobApplicationRepository.getMyApplications(accessToken)
            .then(data => setItems(data || []))
            .catch(e => {
                if (e.status === 401) { onUnauthorized(); return; }
                setError(e.message || '불러오기 실패');
            })
            .finally(() => setLoading(false));
    }, [accessToken, onUnauthorized]);

    function handleCancelClick(e, item) {
        e.stopPropagation();
        setConfirmItem(item);
    }

    async function handleConfirmCancel() {
        const item = confirmItem;
        setConfirmItem(null);
        setCancelling(item.id);
        try {
            await jobApplicationRepository.cancel(item.id, accessToken);
            setItems(prev => prev.filter(i => i.id !== item.id));
        } catch (err) {
            if (err.status === 401) { onUnauthorized(); return; }
            alert(err.message || '신청 취소에 실패했습니다.');
        } finally {
            setCancelling(null);
        }
    }

    const APPLICANT_TYPE_LABEL = { AGENCY_CEO: '대리점 대표 모집', HR_MANAGER: '채용 담당자 모집' };
    const STATUS_LABEL = { REGISTERED: '모집중', MATCHED: '매칭완료', CLOSED: '마감' };

    return (
        <div className="ap-panel">
            <div className="ap-section-title">보낸 채용신청</div>
            {loading && <div className="ap-offer-empty">불러오는 중...</div>}
            {!loading && error && <div className="ap-offer-empty" style={{ color: '#e53935' }}>{error}</div>}
            {!loading && !error && items.length === 0 && (
                <div className="ap-offer-empty">아직 신청한 구인 모집이 없습니다.</div>
            )}
            {!loading && !error && items.length > 0 && (
                <div className="ap-post-table ap-post-table--applied">
                <div className="ap-post-table-inner">
                    <div className="ap-post-header">
                        <span className="ap-ph-main">신청 구인글</span>
                        <span className="ap-ph-date">신청일</span>
                        <span className="ap-ph-status">모집 상태</span>
                        <span className="ap-ph-status">신청 상태</span>
                    </div>
                    {items.map(item => (
                        <div
                            key={item.id}
                            className="ap-post-row"
                            onClick={() => navigate(`/premium/job-board/hire/${item.recruitmentRequestId}`)}
                        >
                            <div className="ap-pr-main">
                                <span className="ap-pr-title">{item.companyName || '회사명 미상'}</span>
                                <span className="ap-pr-sub">
                                    {[
                                        APPLICANT_TYPE_LABEL[item.applicantType] || item.applicantType,
                                        item.companyAddress?.split(' ').slice(0, 2).join(' ')
                                    ].filter(Boolean).join(' · ')}
                                </span>
                            </div>
                            <div className="ap-pr-info">
                                <span className="ap-pr-date">{item.appliedAt ? String(item.appliedAt).slice(0, 10) : '-'}</span>
                                <span className={`ap-recv-status ${item.status === 'MATCHED' ? 'ap-recv-status--pass' : item.status === 'CLOSED' ? 'ap-recv-status--rejected' : 'ap-recv-status--pending'}`}>
                                    {STATUS_LABEL[item.status] || '-'}
                                </span>
                                <span className={`ap-recv-status ${item.applicationStatus === 'CANCELLED' ? 'ap-recv-status--rejected' : 'ap-recv-status--pass'}`}>
                                    {APPLICATION_STATUS_LABEL[item.applicationStatus] || '신청함'}
                                </span>
                            </div>
                            {item.applicationStatus !== 'CANCELLED' && (
                                <button
                                    className="ap-recv-link-btn"
                                    style={{ borderColor: '#e53935', color: '#e53935' }}
                                    onClick={e => handleCancelClick(e, item)}
                                    disabled={cancelling === item.id}
                                >
                                    {cancelling === item.id ? '처리중...' : '취소하기'}
                                </button>
                            )}
                        </div>
                    ))}
                </div>
                </div>
            )}

            {confirmItem && (
                <div className="ap-modal-overlay" onClick={() => setConfirmItem(null)}>
                    <div className="ap-modal" onClick={e => e.stopPropagation()}>
                        <div className="ap-modal-title">채용신청 취소</div>
                        <div className="ap-modal-body">
                            <strong>{confirmItem.companyName || '해당 기업'}</strong>의 구인 모집 신청을
                            취소하시겠습니까?
                        </div>
                        <div className="ap-modal-actions">
                            <button className="ap-modal-btn ap-modal-btn--secondary" onClick={() => setConfirmItem(null)}>
                                돌아가기
                            </button>
                            <button className="ap-modal-btn ap-modal-btn--danger" onClick={handleConfirmCancel}>
                                취소하기
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ── 받은 신청서 패널 (OWNER·MANAGER) ───────────── */
function ReceivedApplicationsPanel({ accessToken, onUnauthorized }) {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!accessToken) { onUnauthorized(); return; }
        jobApplicationRepository.getReceivedApplications(accessToken)
            .then(data => setItems(data || []))
            .catch(e => {
                if (e.status === 401) { onUnauthorized(); return; }
                setError(e.message || '불러오기 실패');
            })
            .finally(() => setLoading(false));
    }, [accessToken, onUnauthorized]);

    const APPLICANT_TYPE_LABEL = {
        AGENCY_CEO: '보험 대리점 대표',
        HR_MANAGER: '채용 담당자',
        INSURANCE_EDU_MANAGER: '설계매니저/교육매니저',
        INSURANCE_PLANNER: '보험 설계사',
    };
    const EXP_LABEL = {
        NEW: '신입', EXPERIENCED: '경력', ANY: '신입/경력',
    };

    const formatPhone = (phone) => {
        if (!phone) return '-';
        const p = phone.replace(/\D/g, '');
        if (p.length === 11) return `${p.slice(0,3)}-${p.slice(3,7)}-${p.slice(7)}`;
        if (p.length === 10) return `${p.slice(0,3)}-${p.slice(3,6)}-${p.slice(6)}`;
        return phone;
    };

    const maskPhone = (phone) => {
        const formatted = formatPhone(phone);
        if (formatted === '-') return '-';
        // 010-1234-5678 → 010-****-5678
        return formatted.replace(/(\d{2,3}-)\d{3,4}(-\d{4})/, '$1****$2');
    };

    return (
        <div className="ap-panel">
            <div className="ap-section-title">받은 채용신청</div>
            {loading && <div className="ap-offer-empty">불러오는 중...</div>}
            {!loading && error && <div className="ap-offer-empty" style={{ color: '#e53935' }}>{error}</div>}
            {!loading && !error && items.length === 0 && (
                <div className="ap-offer-empty">아직 들어온 신청서가 없습니다.</div>
            )}
            {!loading && !error && items.length > 0 && (
                <div className="ap-offer-list">
                    {items.map(item => (
                        <div key={item.id} className="ap-recv-card">
                            {/* 상단: 직무 배지 + 상태 + 날짜 */}
                            <div className="ap-recv-header">
                                <div className="ap-recv-header-badges">
                                    <span className="ap-recv-type-badge">
                                        {APPLICANT_TYPE_LABEL[item.applicantType] || item.applicantType || '직무 미상'}
                                    </span>
                                    {item.applicantWithdrawn && (
                                        <span className="ap-recv-withdrawn-badge">탈퇴 회원</span>
                                    )}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                                    <span className={`ap-recv-status ${item.status === 'CANCELLED' ? 'ap-recv-status--rejected' : 'ap-recv-status--pass'}`}>
                                        {APPLICATION_STATUS_LABEL[item.status] || '신청함'}
                                    </span>
                                    <span className="ap-recv-date">{item.appliedAt ? String(item.appliedAt).slice(0, 10) : ''}</span>
                                </div>
                            </div>
                            {/* 신청자 구직 프로필 */}
                            <div className="ap-recv-info-grid">
                                {item.applicantName && (
                                    <div className="ap-recv-info-row">
                                        <span className="ap-recv-info-label">이름</span>
                                        <span className="ap-recv-info-value ap-recv-info-value--name">{item.applicantName}</span>
                                    </div>
                                )}
                                {item.jobExperienceLevel && (
                                    <div className="ap-recv-info-row">
                                        <span className="ap-recv-info-label">경력</span>
                                        <span className="ap-recv-info-value">{EXP_LABEL[item.jobExperienceLevel] || item.jobExperienceLevel}</span>
                                    </div>
                                )}
                                {item.applicantAddress && (
                                    <div className="ap-recv-info-row">
                                        <span className="ap-recv-info-label">지역</span>
                                        <span className="ap-recv-info-value">{item.applicantAddress.split(' ').slice(0, 2).join(' ')}</span>
                                    </div>
                                )}
                                <div className="ap-recv-info-row">
                                    <span className="ap-recv-info-label">이메일</span>
                                    <span className="ap-recv-info-value">{maskEmail(item.applicantEmail)}</span>
                                </div>
                                <div className="ap-recv-info-row">
                                    <span className="ap-recv-info-label">연락처</span>
                                    <span className="ap-recv-info-value">{maskPhone(item.applicantPhone)}</span>
                                </div>
                            </div>
                            {/* 메시지 */}
                            {item.message && (
                                <div className="ap-recv-message">
                                    <span className="ap-recv-info-label">메시지</span>
                                    <p className="ap-recv-message-text">"{item.message}"</p>
                                </div>
                            )}
                            {/* 푸터: 링크 + 상태변경 버튼 */}
                            <div className="ap-recv-footer">
                                <div className="ap-recv-footer-right">
                                    <button
                                        className="ap-recv-link-btn ap-recv-link-btn--primary"
                                        onClick={e => { e.stopPropagation(); navigate(`/account/received/${item.id}`, { state: { application: item } }); }}
                                    >
                                        지원서 보기
                                    </button>
                                    <button
                                        className="ap-recv-link-btn"
                                        onClick={() => navigate(`/premium/job-board/hire/${item.recruitmentRequestId}`)}
                                    >
                                        모집글 보기
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/* ── 내 구직글 패널 (JOB_SEEKER) ────────────────────── */
function MyJobSeekerPostsPanel({ accessToken, onUnauthorized }) {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [toggling, setToggling] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [confirmItem, setConfirmItem] = useState(null);

    const STATUS_LABEL = { REG: '구직중', MATCHED: '매칭완료', CLOSED: '마감' };
    const STATUS_CLASS = { REG: 'ap-ps-active', MATCHED: 'ap-ps-matched', CLOSED: 'ap-ps-closed' };

    useEffect(() => {
        if (!accessToken) { onUnauthorized(); return; }
        jobSeekerRequestRepository.getMyPosts(accessToken)
            .then(data => setItems(data || []))
            .catch(e => {
                if (e.status === 401) { onUnauthorized(); return; }
                setError(e.message || '불러오기 실패');
            })
            .finally(() => setLoading(false));
    }, [accessToken, onUnauthorized]);

    async function handleToggleActive(e, item) {
        e.stopPropagation();
        setToggling(item.id);
        try {
            await jobSeekerRequestRepository.setActive(item.id, !item.isActive, accessToken);
            setItems(prev => prev.map(i => i.id === item.id ? { ...i, isActive: !i.isActive } : i));
        } catch (err) {
            if (err.status === 401) { onUnauthorized(); return; }
            alert(err.message || '상태 변경에 실패했습니다.');
        } finally {
            setToggling(null);
        }
    }

    async function handleConfirmDelete() {
        const item = confirmItem;
        setConfirmItem(null);
        setDeleting(item.id);
        try {
            await jobSeekerRequestRepository.deleteById(item.id, accessToken);
            setItems(prev => prev.filter(i => i.id !== item.id));
        } catch (err) {
            if (err.status === 401) { onUnauthorized(); return; }
            alert(err.message || '삭제에 실패했습니다.');
        } finally {
            setDeleting(null);
        }
    }

    return (
        <div className="ap-panel">
            <div className="ap-section-title">내 구직글</div>
            {loading && <div className="ap-offer-empty">불러오는 중...</div>}
            {!loading && error && <div className="ap-offer-empty" style={{ color: '#e53935' }}>{error}</div>}
            {!loading && !error && items.length === 0 && (
                <div className="ap-offer-empty">등록한 구직글이 없습니다.</div>
            )}
            {!loading && !error && items.length > 0 && (
                <div className="ap-post-table">
                <div className="ap-post-table-inner">
                    <div className="ap-post-header">
                        <span className="ap-ph-main">구직글</span>
                        <span className="ap-ph-date">등록일</span>
                        <span className="ap-ph-status">진행 상태</span>
                        <span className="ap-ph-pub">공개</span>
                        <span className="ap-ph-action"></span>
                    </div>
                    {items.map(item => (
                        <div
                            key={item.id}
                            className={`ap-post-row${item.isActive === false ? ' ap-post-row--paused' : ''}`}
                            onClick={() => navigate(`/premium/job-board/seeker/${item.id}`, { state: { isOwner: true } })}
                        >
                            <div className="ap-pr-main">
                                <span className="ap-pr-title">{item.name || '이름 미상'}</span>
                                <span className="ap-pr-sub">
                                    {[item.address?.split(' ').slice(0, 2).join(' '), item.jobExperienceLevel].filter(Boolean).join(' · ')}
                                </span>
                            </div>
                            <div className="ap-pr-info">
                                <span className="ap-pr-date">{item.createdAt ? String(item.createdAt).slice(0, 10) : '-'}</span>
                                <span className={`ap-pr-status ${STATUS_CLASS[item.status] || ''}`}>
                                    {STATUS_LABEL[item.status] || item.status}
                                </span>
                                <span className="ap-pr-pub">
                                    {item.isActive === false
                                        ? <span className="ap-pub-off">중지됨</span>
                                        : <span className="ap-pub-on">공개중</span>
                                    }
                                </span>
                            </div>
                            <div className="ap-pr-actions" onClick={e => e.stopPropagation()}>
                                <button
                                    className={`ap-pr-action${item.isActive === false ? ' ap-pr-action--resume' : ''}`}
                                    onClick={e => handleToggleActive(e, item)}
                                    disabled={toggling === item.id || deleting === item.id}
                                >
                                    {toggling === item.id ? '...' : item.isActive === false ? '재개' : '중지'}
                                </button>
                                <button
                                    className="ap-pr-action ap-pr-action--cancel"
                                    onClick={() => setConfirmItem(item)}
                                    disabled={toggling === item.id || deleting === item.id}
                                >
                                    {deleting === item.id ? '...' : '삭제'}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
                </div>
            )}

            {confirmItem && (
                <div className="ap-modal-overlay" onClick={() => setConfirmItem(null)}>
                    <div className="ap-modal" onClick={e => e.stopPropagation()}>
                        <div className="ap-modal-title">구직글 삭제</div>
                        <div className="ap-modal-body">
                            <strong>{confirmItem.name || '이 구직글'}</strong>을(를) 삭제하시겠습니까?
                            삭제 후에는 되돌릴 수 없습니다.
                        </div>
                        <div className="ap-modal-actions">
                            <button className="ap-modal-btn ap-modal-btn--secondary" onClick={() => setConfirmItem(null)}>취소</button>
                            <button className="ap-modal-btn ap-modal-btn--danger" onClick={handleConfirmDelete}>삭제하기</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ── 내 구인글 패널 (COMPANY OWNER·MANAGER) ─────────── */
function MyRecruitmentPostsPanel({ accessToken, onUnauthorized }) {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [toggling, setToggling] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [confirmItem, setConfirmItem] = useState(null);

    const STATUS_LABEL = { REGISTERED: '모집중', MATCHED: '매칭완료', CLOSED: '마감' };
    const STATUS_CLASS = { REGISTERED: 'ap-ps-active', MATCHED: 'ap-ps-matched', CLOSED: 'ap-ps-closed' };
    const APPLICANT_TYPE_LABEL = { AGENCY_CEO: '대리점 대표', HR_MANAGER: '채용 담당자' };
    const targetTypeLabel = (t) => (t === 'PLANNER' ? '보험 설계사 채용' : null);

    useEffect(() => {
        if (!accessToken) { onUnauthorized(); return; }
        recruitmentRequestRepository.getMyPosts(accessToken)
            .then(data => setItems(data || []))
            .catch(e => {
                if (e.status === 401) { onUnauthorized(); return; }
                setError(e.message || '불러오기 실패');
            })
            .finally(() => setLoading(false));
    }, [accessToken, onUnauthorized]);

    async function handleToggleActive(e, item) {
        e.stopPropagation();
        setToggling(item.id);
        try {
            await recruitmentRequestRepository.setActive(item.id, !item.isActive, accessToken);
            setItems(prev => prev.map(i => i.id === item.id ? { ...i, isActive: !i.isActive } : i));
        } catch (err) {
            if (err.status === 401) { onUnauthorized(); return; }
            alert(err.message || '상태 변경에 실패했습니다.');
        } finally {
            setToggling(null);
        }
    }

    async function handleConfirmDelete() {
        const item = confirmItem;
        setConfirmItem(null);
        setDeleting(item.id);
        try {
            await recruitmentRequestRepository.deleteById(item.id, accessToken);
            setItems(prev => prev.filter(i => i.id !== item.id));
        } catch (err) {
            if (err.status === 401) { onUnauthorized(); return; }
            alert(err.message || '삭제에 실패했습니다.');
        } finally {
            setDeleting(null);
        }
    }

    return (
        <div className="ap-panel">
            <div className="ap-section-title">내 구인글</div>
            {loading && <div className="ap-offer-empty">불러오는 중...</div>}
            {!loading && error && <div className="ap-offer-empty" style={{ color: '#e53935' }}>{error}</div>}
            {!loading && !error && items.length === 0 && (
                <div className="ap-offer-empty">등록한 구인글이 없습니다.</div>
            )}
            {!loading && !error && items.length > 0 && (
                <div className="ap-post-table">
                <div className="ap-post-table-inner">
                    <div className="ap-post-header">
                        <span className="ap-ph-main">구인글</span>
                        <span className="ap-ph-date">등록일</span>
                        <span className="ap-ph-status">진행 상태</span>
                        <span className="ap-ph-pub">공개</span>
                        <span className="ap-ph-action"></span>
                    </div>
                    {items.map(item => (
                        <div
                            key={item.id}
                            className={`ap-post-row${item.isActive === false ? ' ap-post-row--paused' : ''}`}
                            onClick={() => navigate(`/premium/job-board/hire/${item.id}`)}
                        >
                            <div className="ap-pr-main">
                                <span className="ap-pr-title">{item.companyName || '회사명 미상'}</span>
                                <span className="ap-pr-sub">
                                    {[targetTypeLabel(item.targetType), APPLICANT_TYPE_LABEL[item.applicantType], item.companyAddress?.split(' ').slice(0, 2).join(' ')].filter(Boolean).join(' · ')}
                                </span>
                            </div>
                            <div className="ap-pr-info">
                                <span className="ap-pr-date">{item.createdAt ? String(item.createdAt).slice(0, 10) : '-'}</span>
                                <span className={`ap-pr-status ${STATUS_CLASS[item.status] || ''}`}>
                                    {STATUS_LABEL[item.status] || item.status}
                                </span>
                                <span className="ap-pr-pub">
                                    {item.isActive === false
                                        ? <span className="ap-pub-off">중지됨</span>
                                        : <span className="ap-pub-on">공개중</span>
                                    }
                                </span>
                            </div>
                            <div className="ap-pr-actions" onClick={e => e.stopPropagation()}>
                                <button
                                    className="ap-pr-action ap-pr-action--edit"
                                    onClick={() => navigate(
                                        item.targetType === 'PLANNER'
                                            ? `/premium/company/planner/${item.applicantType === 'HR_MANAGER' ? 'manager' : 'rep'}`
                                            : item.applicantType === 'AGENCY_CEO' ? '/premium/company/rep' : '/premium/company/manager',
                                        { state: { editMode: true, postId: item.id, initialData: item } }
                                    )}
                                >
                                    수정
                                </button>
                                <button
                                    className={`ap-pr-action${item.isActive === false ? ' ap-pr-action--resume' : ''}`}
                                    onClick={e => handleToggleActive(e, item)}
                                    disabled={toggling === item.id || deleting === item.id}
                                >
                                    {toggling === item.id ? '...' : item.isActive === false ? '재개' : '중지'}
                                </button>
                                <button
                                    className="ap-pr-action ap-pr-action--cancel"
                                    onClick={() => setConfirmItem(item)}
                                    disabled={toggling === item.id || deleting === item.id}
                                >
                                    {deleting === item.id ? '...' : '삭제'}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
                </div>
            )}

            {confirmItem && (
                <div className="ap-modal-overlay" onClick={() => setConfirmItem(null)}>
                    <div className="ap-modal" onClick={e => e.stopPropagation()}>
                        <div className="ap-modal-title">구인글 삭제</div>
                        <div className="ap-modal-body">
                            <strong>{confirmItem.companyName || '이 구인글'}</strong>을(를) 삭제하시겠습니까?
                            삭제 후에는 되돌릴 수 없습니다.
                        </div>
                        <div className="ap-modal-actions">
                            <button className="ap-modal-btn ap-modal-btn--secondary" onClick={() => setConfirmItem(null)}>취소</button>
                            <button className="ap-modal-btn ap-modal-btn--danger" onClick={handleConfirmDelete}>삭제하기</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ── 보낸 채용제안 패널 (OWNER·MANAGER) ─────────── */
function SentJobOffersPanel({ accessToken, onUnauthorized }) {
    const navigate = useNavigate();
    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [withdrawing, setWithdrawing] = useState(null);
    const [confirmItem, setConfirmItem] = useState(null);

    useEffect(() => {
        if (!accessToken) { onUnauthorized(); return; }
        jobOfferRepository.getSentOffers(accessToken)
            .then(data => setOffers(data || []))
            .catch(e => {
                if (e.status === 401) { onUnauthorized(); return; }
                setError(e.message || '불러오기 실패');
            })
            .finally(() => setLoading(false));
    }, [accessToken, onUnauthorized]);

    async function handleWithdraw() {
        const offer = confirmItem;
        setConfirmItem(null);
        setWithdrawing(offer.id);
        try {
            await jobOfferRepository.withdrawOffer(accessToken, offer.id);
            setOffers(prev => prev.map(o => o.id === offer.id ? { ...o, status: 'WITHDRAWN' } : o));
        } catch (err) {
            if (err.status === 401) { onUnauthorized(); return; }
            alert(err.message || '취소에 실패했습니다.');
        } finally {
            setWithdrawing(null);
        }
    }

    const OFFER_STATUS_LABEL = { OFFERED: '제안함', WITHDRAWN: '제안 취소됨' };

    return (
        <div className="ap-panel">
            <div className="ap-section-title">보낸 채용제안</div>
            {loading && <div className="ap-offer-empty">불러오는 중...</div>}
            {!loading && error && <div className="ap-offer-empty" style={{ color: '#e53935' }}>{error}</div>}
            {!loading && !error && offers.length === 0 && (
                <div className="ap-offer-empty">아직 보낸 채용제안이 없습니다.</div>
            )}
            {!loading && !error && offers.length > 0 && (
                <div className="ap-post-table ap-post-table--sent-offers">
                <div className="ap-post-table-inner">
                    <div className="ap-post-header">
                        <span className="ap-ph-main">제안 대상</span>
                        <span className="ap-ph-date">제안일</span>
                        <span className="ap-ph-status">제안 상태</span>
                    </div>
                    {offers.map(offer => (
                        <div
                            key={offer.id}
                            className={`ap-post-row${offer.status === 'WITHDRAWN' ? ' ap-post-row--paused' : ''}`}
                            style={!offer.jobSeekerRequestId ? { cursor: 'default' } : undefined}
                            onClick={offer.jobSeekerRequestId ? () => navigate(`/premium/job-board/seeker/${offer.jobSeekerRequestId}`) : undefined}
                        >
                            <div className="ap-pr-main">
                                <span className="ap-pr-title">{offer.seekerName || '이름 미제공'}</span>
                                <span
                                    className="ap-pr-sub"
                                    title={offer.message || undefined}
                                >
                                    {[
                                        offer.seekerAddress?.split(' ').slice(0, 2).join(' '),
                                        offer.message ? `"${offer.message}"` : null,
                                    ].filter(Boolean).join(' · ')}
                                </span>
                            </div>
                            <div className="ap-pr-info">
                                <span className="ap-pr-date">{offer.sentAt ? String(offer.sentAt).slice(0, 10) : '-'}</span>
                                <span className={`ap-recv-status ${offer.status === 'WITHDRAWN' ? 'ap-recv-status--rejected' : 'ap-recv-status--pass'}`}>
                                    {OFFER_STATUS_LABEL[offer.status] || '제안함'}
                                </span>
                            </div>
                            <div className="ap-pr-actions" onClick={e => e.stopPropagation()}>
                                {offer.recruitmentRequestId && (
                                    <button className="ap-recv-link-btn" onClick={() => navigate(`/premium/job-board/hire/${offer.recruitmentRequestId}`)}>
                                        모집글 보기
                                    </button>
                                )}
                                {offer.status !== 'WITHDRAWN' && (
                                    <button
                                        className="ap-recv-link-btn"
                                        style={{ borderColor: '#e53935', color: '#e53935' }}
                                        disabled={withdrawing === offer.id}
                                        onClick={() => setConfirmItem(offer)}
                                    >
                                        {withdrawing === offer.id ? '철회중...' : '제안 철회'}
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
                </div>
            )}

            {confirmItem && (
                <div className="ap-modal-overlay" onClick={() => setConfirmItem(null)}>
                    <div className="ap-modal" onClick={e => e.stopPropagation()}>
                        <div className="ap-modal-title">제안 철회</div>
                        <div className="ap-modal-body">
                            <strong>{confirmItem.seekerName || '해당 구직자'}</strong>에게 보낸 채용제안을 취소하시겠습니까?
                        </div>
                        <div className="ap-modal-actions">
                            <button className="ap-modal-btn ap-modal-btn--secondary" onClick={() => setConfirmItem(null)}>돌아가기</button>
                            <button className="ap-modal-btn ap-modal-btn--danger" onClick={handleWithdraw}>철회하기</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ── CSS ─────────────────────────────────────────── */
const _css = `
.ap-root {
    display: flex;
    min-height: calc(100vh - 80px);
    background: #f4f5f7;
}

/* ── 사이드바 (공통) ── */
.ap-sidebar {
    width: 200px;
    flex-shrink: 0;
    background: #fff;
    border-right: 1px solid #e8e8e8;
    display: flex;
    flex-direction: column;
}
.ap-tab-list {
    display: flex;
    flex-direction: column;
    padding: 24px 0 0;
}
.ap-tab {
    display: block;
    width: 100%;
    padding: 14px 28px;
    text-align: left;
    background: none;
    border: none;
    font-size: 15px;
    color: #555;
    cursor: pointer;
    transition: color 0.15s;
    position: relative;
}
.ap-tab:hover { color: #1a1a1a; }
.ap-tab--active {
    font-weight: 700;
    color: #1a1a1a;
}
.ap-tab--logout { color: #aaa; font-size: 14px; }
.ap-tab--logout:hover { color: #e53935; }

.ap-sidebar-footer { margin-top: auto; }
.ap-nav-divider { height: 1px; background: #f0f0f0; margin: 8px 0; }

/* 모바일 전용 설정 아이콘 — 데스크톱에서 숨김 */
.ap-tab-settings { display: none; }

/* ── 콘텐츠 ── */
.ap-content {
    flex: 1;
    padding: 40px 40px;
    min-width: 0;
}
.ap-content-inner {
    max-width: 860px;
    margin: 0 auto;
}
.ap-panel {}
.ap-section-title {
    font-size: 16px;
    font-weight: 700;
    color: #1a1a1a;
    margin-bottom: 12px;
}

/* ── 대시보드 프로필 ── */
.ap-dashboard { display: flex; flex-direction: column; gap: 20px; }

.ap-dh-card {
    display: flex; align-items: center; gap: 16px;
    background: #fff; border-radius: 14px;
    padding: 22px 24px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);
}
.ap-dh-avatar {
    width: 48px; height: 48px; border-radius: 50%;
    background: #f16102; color: #fff;
    font-size: 20px; font-weight: 700; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
}
.ap-dh-info { flex: 1; min-width: 0; }
.ap-dh-name  { font-size: 15px; font-weight: 700; color: #1a1a1a; }
.ap-dh-email { font-size: 12px; color: #aaa; margin-top: 2px;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ap-dh-role {
    flex-shrink: 0; font-size: 11px; font-weight: 700;
    padding: 4px 10px; border-radius: 20px;
    background: #e8f5e9; color: #2e7d32;
}
.ap-dh-role--company { background: #fff3e0; color: #e65100; }

.ap-db-section { display: flex; flex-direction: column; gap: 8px; }
.ap-db-label {
    font-size: 11px; font-weight: 700; color: #aaa;
    letter-spacing: 0.06em; text-transform: uppercase;
    padding-left: 2px;
}

/* 활동 타일 */
.ap-db-tiles {
    background: #fff; border-radius: 14px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);
    overflow: hidden;
}
.ap-db-tile {
    display: flex; align-items: center; gap: 12px;
    width: 100%; padding: 15px 20px;
    background: none; border: none; border-bottom: 1px solid #f5f5f5;
    cursor: pointer; text-align: left;
    transition: background 0.12s;
}
.ap-db-tile:last-child { border-bottom: none; }
.ap-db-tile:hover { background: #fafafa; }
.ap-db-tile-icon {
    width: 34px; height: 34px; border-radius: 9px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
}
.ap-icon--blue   { background: #e8f0fe; color: #1a73e8; }
.ap-icon--orange { background: #fff3e0; color: #f16102; }
.ap-icon--green  { background: #e8f5e9; color: #2e7d32; }
.ap-db-tile-name { flex: 1; font-size: 14px; font-weight: 500; color: #2c3e50; }

/* 리스트 */
.ap-list {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);
    overflow: hidden;
}
.ap-list-item {
    display: flex; align-items: center; justify-content: space-between;
    width: 100%; padding: 15px 18px;
    font-size: 14px; color: #2c3e50;
    text-decoration: none;
    background: none; border: none;
    border-bottom: 1px solid #f5f5f5;
    cursor: pointer; transition: background 0.15s; text-align: left;
}
.ap-list-item:last-child { border-bottom: none; }
.ap-list-item:hover { background: #f8f9fa; }
.ap-arrow { color: #ccc; font-size: 18px; }

/* ── 채용제안 ── */
.ap-offer-empty {
    padding: 40px 0; text-align: center;
    color: #bbb; font-size: 14px;
}
.ap-offer-list { display: flex; flex-direction: column; gap: 12px; }
.ap-offer-card {
    background: #fff; border-radius: 12px; padding: 18px 20px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);
    display: flex; flex-direction: column; gap: 8px;
}
.ap-offer-card--clickable { cursor: pointer; transition: box-shadow 0.15s, transform 0.15s; }
.ap-offer-card--clickable:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.10); transform: translateY(-1px); }
.ap-offer-company { font-size: 15px; font-weight: 700; color: #1a1a1a; }
.ap-offer-meta { display: flex; gap: 6px; flex-wrap: wrap; }
.ap-offer-tag {
    font-size: 12px; background: #f0f0f0; color: #666;
    padding: 3px 10px; border-radius: 20px; font-weight: 500;
}
.ap-offer-message {
    font-size: 13px; color: #555; font-style: italic;
    background: #f9f9f9; border-radius: 8px; padding: 10px 12px;
    line-height: 1.6;
}
.ap-offer-footer {
    display: flex; align-items: center; justify-content: space-between;
    margin-top: 4px; flex-wrap: wrap; gap: 6px;
}
.ap-offer-contact { font-size: 13px; color: #555; }
.ap-offer-contact strong { color: #1a1a1a; }
.ap-offer-date { font-size: 12px; color: #bbb; }
.ap-offer-footer-right { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }

/* ── 받은 신청서 카드 ── */
.ap-recv-card {
    background: #fff;
    border: 1px solid #eee;
    border-radius: 14px;
    padding: 18px 20px;
    display: flex;
    flex-direction: column;
    gap: 14px;
    transition: box-shadow 0.15s;
}
.ap-recv-card:hover { box-shadow: 0 2px 10px rgba(0,0,0,0.06); }

.ap-recv-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}
.ap-recv-header-badges {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}
.ap-recv-type-badge {
    display: inline-flex;
    align-items: center;
    background: #fff;
    color: #333;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 500;
    padding: 4px 12px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}
.ap-recv-withdrawn-badge {
    display: inline-flex;
    align-items: center;
    background: #fff;
    color: #bbb;
    border: 1px solid #ebebeb;
    border-radius: 8px;
    font-size: 11px;
    font-weight: 500;
    padding: 4px 10px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}
.ap-recv-date {
    font-size: 12px;
    color: #ccc;
    white-space: nowrap;
    flex-shrink: 0;
    padding: 4px 12px;
}

.ap-recv-info-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px 16px;
    background: #fafafa;
    border-radius: 10px;
    padding: 12px 16px;
}
.ap-recv-info-row {
    display: flex;
    align-items: baseline;
    gap: 8px;
    font-size: 13px;
    min-width: 0;
}
.ap-recv-info-label {
    flex-shrink: 0;
    color: #bbb;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.02em;
}
.ap-recv-info-value {
    color: #222;
    font-weight: 500;
    word-break: break-all;
    font-size: 13px;
}
.ap-recv-info-value--name {
    font-weight: 700;
    font-size: 14px;
    color: #111;
}

.ap-recv-message {
    display: flex;
    flex-direction: column;
    gap: 4px;
}
.ap-recv-message-text {
    font-size: 13px;
    color: #555;
    line-height: 1.6;
    margin: 0;
    padding: 10px 14px;
    background: #fff8f4;
    border-left: 3px solid #f16102;
    border-radius: 0 8px 8px 0;
}

.ap-recv-footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
    border-top: 1px solid #f5f5f5;
    padding-top: 12px;
}
.ap-recv-footer-left {
    display: flex;
    align-items: center;
    gap: 8px;
}
.ap-recv-footer-right {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}
.ap-recv-status {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 500;
    padding: 4px 12px;
    white-space: nowrap;
}
.ap-recv-status--pending { background: #fff; color: #009c06; border: 1px solid #e8e8e8; box-shadow: 0 1px 3px rgba(0,0,0,0.04); }
.ap-recv-status--pass { background: #fff; color: #1565c0; border: 1px solid #e3f2fd; box-shadow: 0 1px 3px rgba(0,0,0,0.04); }
.ap-recv-status--rejected { background: #fff; color: #bbb; border: 1px solid #ebebeb; box-shadow: 0 1px 3px rgba(0,0,0,0.04); }

.ap-recv-link-btn {
    background: #fff;
    border: 1px solid #e8e8e8;
    color: #222;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    padding: 6px 16px;
    cursor: pointer;
    box-shadow: 0 1px 3px rgba(0,0,0,0.04);
    transition: border-color 0.15s, box-shadow 0.15s;
}
.ap-recv-link-btn:hover {
    border-color: #ccc;
    box-shadow: 0 2px 6px rgba(0,0,0,0.08);
}
.ap-recv-link-btn--primary {
    border-color: #e8e8e8;
    color: #222;
}
.ap-recv-link-btn--primary:hover {
    border-color: #ccc;
    color: #222;
    background: #fff;
}
.ap-recv-status-btn {
    flex-shrink: 0;
    padding: 6px 16px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    border: 1px solid #e0e0e0;
    background: #fff;
    color: #555;
    box-shadow: 0 1px 3px rgba(0,0,0,0.04);
    transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
}
.ap-recv-status-btn:hover { border-color: #bbb; box-shadow: 0 2px 6px rgba(0,0,0,0.08); }
.ap-recv-status-btn:disabled { opacity: 0.4; cursor: not-allowed; }

/* ── 내 신청 현황 전용 — 신청 상태 컬럼 확장 ── */
.ap-post-table--applied .ap-post-header,
.ap-post-table--applied .ap-post-row {
    grid-template-columns: 1fr 90px 80px 104px 88px;
    column-gap: 10px;
    min-width: 540px;
}
.ap-post-table--applied .ap-recv-status {
    width: fit-content;
    justify-self: center;
}
.ap-post-table--applied .ap-recv-link-btn {
    justify-self: end;
}
@media (max-width: 640px) {
    .ap-post-table--applied .ap-post-row {
        min-width: 0;
        column-gap: 8px;
        grid-template-columns: 1fr auto;
        grid-template-rows: auto auto;
    }
    .ap-post-table--applied .ap-recv-link-btn {
        width: auto;
        grid-column: 2;
        grid-row: 1;
        align-self: start;
    }
}

/* ── 보낸 채용제안 전용 — 단일 상태 컬럼 + 액션 버튼 그룹 ── */
.ap-post-table--sent-offers .ap-post-header,
.ap-post-table--sent-offers .ap-post-row {
    grid-template-columns: 1fr 90px 104px 220px;
    column-gap: 10px;
    min-width: 640px;
}
.ap-post-table--sent-offers .ap-recv-status {
    width: fit-content;
    justify-self: center;
}
.ap-post-table--sent-offers .ap-pr-sub {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
}
.ap-post-table--sent-offers .ap-pr-actions {
    justify-self: end;
    flex-wrap: wrap;
    justify-content: flex-end;
}
@media (max-width: 640px) {
    .ap-post-table--sent-offers .ap-post-row {
        min-width: 0;
        column-gap: 8px;
        grid-template-columns: 1fr auto;
        grid-template-rows: auto auto;
    }
}

/* ── 내 구직글/구인글 테이블 ── */
.ap-post-table {
    border-radius: 12px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);
    overflow: hidden;
}
.ap-post-table-inner {
    overflow-x: auto;
    background: #fff;
}
.ap-post-header {
    display: grid;
    grid-template-columns: 1fr 90px 80px 64px 110px;
    align-items: center;
    padding: 10px 20px;
    background: #f8f9fa;
    border-bottom: 1px solid #f0f0f0;
    font-size: 11px; font-weight: 700;
    color: #aaa; letter-spacing: 0.04em; text-transform: uppercase;
    min-width: 614px;
}
.ap-post-row {
    display: grid;
    grid-template-columns: 1fr 90px 80px 64px 110px;
    align-items: center;
    padding: 14px 20px;
    border-bottom: 1px solid #f5f5f5;
    cursor: pointer;
    transition: background 0.12s;
    min-width: 614px;
}
.ap-post-row:last-child { border-bottom: none; }
.ap-post-row:hover { background: #fafafa; }
.ap-post-row--paused { background: #fafafa; }
.ap-post-row--paused:hover { background: #f5f5f5; }

/* 데스크톱: wrapper를 grid에서 투명하게 */
.ap-pr-info { display: contents; }

.ap-pr-main { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.ap-pr-title {
    font-size: 14px; font-weight: 600; color: #1a1a1a;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.ap-post-row--paused .ap-pr-title { color: #999; }
.ap-pr-sub { font-size: 12px; color: #bbb; }

.ap-pr-date { font-size: 12px; color: #aaa; }

.ap-pr-status { font-size: 12px; font-weight: 600; }
.ap-ps-active { color: #2e7d32; }
.ap-ps-matched { color: #1565c0; }
.ap-ps-closed { color: #999; }
.ap-offer-status.ap-ps-active { background: #e8f5e9; color: #2e7d32; }
.ap-offer-status.ap-ps-matched { background: #e3f2fd; color: #1565c0; }
.ap-offer-status.ap-ps-closed { background: #f5f5f5; color: #999; }

.ap-offer-card--withdrawn { opacity: 0.65; }
.ap-offer-card-header { display: flex; align-items: center; gap: 8px; }
.ap-offer-badge {
    display: inline-block; padding: 2px 8px; border-radius: 4px;
    font-size: 11px; font-weight: 600; white-space: nowrap;
}
.ap-offer-badge--withdrawn { background: #f5f5f5; color: #999; border: 1px solid #e0e0e0; }

.ap-pr-pub { font-size: 12px; }
.ap-pub-on { color: #2e7d32; font-weight: 600; }
.ap-pub-off { color: #bbb; font-weight: 600; }

.ap-pr-action {
    padding: 5px 10px; border-radius: 6px; font-size: 12px;
    font-weight: 700; cursor: pointer; border: 1.5px solid #e0e0e0;
    background: #fff; color: #aaa;
    transition: border-color 0.15s, color 0.15s, background 0.15s;
    white-space: nowrap;
}
.ap-pr-action:hover { border-color: #bbb; color: #555; }
.ap-pr-action--resume { border-color: #f16102; color: #f16102; background: #fff8f4; }
.ap-pr-action--resume:hover { background: #f16102; color: #fff; }
.ap-pr-action--cancel { border-color: #e53935; color: #e53935; background: #fff5f5; }
.ap-pr-action--cancel:hover { background: #e53935; color: #fff; }
.ap-pr-action--edit { border-color: #90a4ae; color: #607d8b; background: #f5f8fa; }
.ap-pr-action--edit:hover { background: #607d8b; color: #fff; border-color: #607d8b; }
.ap-pr-actions { display: flex; gap: 6px; align-items: center; }

/* ── 확인 모달 ── */
.ap-modal-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,0.45);
    display: flex; align-items: center; justify-content: center;
    z-index: 1000;
}
.ap-modal {
    background: #fff; border-radius: 16px; padding: 28px 24px 20px;
    width: min(360px, 90vw); box-shadow: 0 8px 32px rgba(0,0,0,0.18);
    display: flex; flex-direction: column; gap: 12px;
}
.ap-modal-title { font-size: 16px; font-weight: 700; color: #1a1a1a; }
.ap-modal-body { font-size: 14px; color: #444; line-height: 1.7; }
.ap-modal-body strong { color: #1a1a1a; }
.ap-modal-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 4px; }
.ap-modal-btn {
    padding: 9px 18px; border-radius: 8px; font-size: 14px; font-weight: 600;
    border: none; cursor: pointer; transition: background 0.15s;
}
.ap-modal-btn--secondary { background: #f0f0f0; color: #555; }
.ap-modal-btn--secondary:hover { background: #e0e0e0; }
.ap-modal-btn--primary { background: #1565c0; color: #fff; }
.ap-modal-btn--primary:hover { background: #0d47a1; }
.ap-modal-btn--primary:disabled { opacity: 0.4; cursor: not-allowed; }
.ap-modal-btn--danger { background: #e53935; color: #fff; }
.ap-modal-btn--danger:hover { background: #c62828; }
.ap-pr-action:disabled { opacity: 0.4; cursor: not-allowed; }

/* ── 회원 탈퇴 ── */
.ap-list-item--danger { color: #e53935; }
.ap-list-item--danger:hover { background: #fff5f5; color: #c62828; }

.ap-modal-warn {
    font-size: 13px; color: #92400e; line-height: 1.6;
    background: #fffbeb; border: 1px solid #fde68a;
    border-left: 3px solid #f59e0b;
    border-radius: 8px; padding: 10px 12px;
}
.ap-modal-owner-note {
    margin: 8px 0 0; font-size: 12px; color: #888; line-height: 1.5;
}
.ap-modal-field { margin-top: 14px; display: flex; flex-direction: column; gap: 6px; }
.ap-modal-label { font-size: 13px; font-weight: 600; color: #444; }
.ap-modal-input {
    height: 44px; width: 100%; box-sizing: border-box;
    border: 1.5px solid #e0e0e0; border-radius: 8px;
    padding: 0 14px; font-size: 14px; color: #1c1814;
    outline: none; transition: border-color 0.2s;
}
.ap-modal-input:focus { border-color: #f16102; }
.ap-modal-input--error { border-color: #e53935; }
.ap-modal-error {
    margin: 6px 0 0; font-size: 13px; color: #e53935;
    animation: alertSlideIn 0.2s ease;
}
.ap-modal-btn:disabled { opacity: 0.6; cursor: not-allowed; }

@media (max-width: 640px) {
    .ap-post-header { display: none; }
    .ap-post-row {
        display: grid;
        grid-template-columns: 1fr auto;
        grid-template-rows: auto auto;
        gap: 6px 8px;
        padding: 14px 16px;
        min-width: 0;
    }
    .ap-pr-main  { grid-column: 1; grid-row: 1; }
    .ap-pr-action { grid-column: 2; grid-row: 1; align-self: start; margin-top: 2px; }

    /* 모바일: wrapper를 flex row로 전환 */
    .ap-pr-info {
        display: flex;
        grid-column: 1 / -1;
        grid-row: 2;
        gap: 6px;
        align-items: center;
        flex-wrap: wrap;
    }
    .ap-pr-date::after  { content: '·'; margin-left: 6px; color: #ddd; }
    .ap-pr-status::after { content: '·'; margin-left: 6px; color: #ddd; }
}

/* ── 모바일 ── */
@media (max-width: 640px) {
    .ap-root { flex-direction: column; }

    .ap-sidebar {
        width: 100%;
        flex-direction: row;
        align-items: stretch;
        border-right: none;
        border-bottom: 2px solid #e8e8e8;
        position: sticky;
        top: 80px;
        z-index: 100;
        background: #fff;
    }
    .ap-tab-list {
        flex-direction: row;
        flex: 1;
        padding: 0;
        overflow-x: auto;
        scrollbar-width: none;
    }
    .ap-tab-list::-webkit-scrollbar { display: none; }

    .ap-tab {
        padding: 14px 18px;
        white-space: nowrap;
        font-size: 14px;
        border-bottom: 2px solid transparent;
        margin-bottom: -2px;
    }
    .ap-tab--active {
        color: #1a1a1a;
        border-bottom-color: #1a1a1a;
    }

    /* 데스크톱 전용 요소 숨김 */
    .ap-sidebar-footer { display: none; }

    /* 설정(로그아웃) 아이콘 — 모바일 우측 */
    .ap-tab-settings {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0 16px;
        background: none;
        border: none;
        border-left: 1px solid #f0f0f0;
        color: #888;
        cursor: pointer;
        flex-shrink: 0;
    }
    .ap-tab-settings:hover { color: #e53935; }

    .ap-content { padding: 20px 16px; }

    /* 받은 채용신청 카드 — 정보 그리드 1열 전환 */
    .ap-recv-info-grid {
        grid-template-columns: 1fr;
        gap: 8px 0;
    }

    /* 받은 채용신청 카드 — 푸터 세로 배치 */
    .ap-recv-footer {
        flex-direction: column;
        align-items: stretch;
    }
    .ap-recv-footer-right {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 6px;
    }
    /* 상태 변경 버튼 전체 너비 */
    .ap-recv-status-btn {
        grid-column: 1 / -1;
    }
}
`;

if (!document.head.querySelector('[data-ap]')) {
    const s = document.createElement('style');
    s.setAttribute('data-ap', '1');
    s.textContent = _css;
    document.head.appendChild(s);
}
